import { useAuth, useSoundNotif } from '@/shared/context'
import useSignalR from '@/shared/hooks/useSignalR'
import { useAppDispatch } from '@/shared/redux/hooks'
import { setConnection } from '@/shared/redux/slices/SignalRSlice.slice'
import {
  getListSystemKey,
  setAppInitialized,
  setListTabs,
  setUserInfo,
} from '@/shared/redux/slices/appGlobal.slice'
import CommonService from '@/shared/services/CommonService'
import IdentityService from '@/shared/services/IdentityService'
import { STORAGE, getLocalStorage, setLocalStorage } from '@/shared/utils/storage'
import { type ReactNode, useEffect } from 'react'

interface DefaultActionProps {
  children?: ReactNode
}

const DefaultAction = ({ children }: DefaultActionProps) => {
  const isLogin = getLocalStorage(STORAGE.TOKEN)

  const { setUser } = useAuth()
  const { isSoundNotif } = useSoundNotif()
  const dispatch = useAppDispatch()

  const { connection, connectionId } = useSignalR(isLogin)

  useEffect(() => {
    const initializeApp = async () => {
      const initPromises = []

      // 1. System Key Promise (always load)
      const systemKeyPromise = (async () => {
        try {
          const res = await CommonService.getSystemKey()
          if (!res.isError) {
            dispatch(getListSystemKey(res.object))
          }
        } catch (_error) {
          console.error('DefaultAction>>getSystemKey error:', _error)
        }
      })()
      initPromises.push(systemKeyPromise)

      // 2. Auth-related Promises (only load if user is logged in)
      if (isLogin) {
        setLocalStorage(
          STORAGE.DEV_MODE,
          !!(typeof window !== 'undefined' && (window as any).env?.API_ROOT)?.IS_DEV
        )

        const getListTabPromise = (async () => {
          try {
            const resp = await IdentityService.getTabs()
            if (resp.isOk) {
              dispatch(
                setListTabs([
                  {
                    categoryId: 0,
                    Description: 'Tổng Quan',
                    isVisitTab: true,
                  },
                  {
                    categoryId: -1,
                    codeKey: 'MyTicket',
                    description: 'MyTicket',
                    isVisitTab: true,
                  },
                  ...resp.object,
                ])
              )
            }
          } catch (_error) {
            console.error('DefaultAction>>getListTab error:', _error)
          }
        })()
        initPromises.push(getListTabPromise)

        const getUserInforPromise = (async () => {
          try {
            const resuf = await IdentityService.getInfo()
            if (resuf.isOk) {
              setLocalStorage(STORAGE.USER_INFO, resuf.object)
              dispatch(setUserInfo(resuf.object))

              setUser(resuf.object)
            }
          } catch (error) {
            console.error('DefaultAction>>updateUserInfor error:', error)
          }
        })()
        initPromises.push(getUserInforPromise)
      }

      // 3. Minimum 2.5s delay
      const timerPromise = new Promise((resolve) => setTimeout(resolve, 2500))

      // Wait for everything to settle
      await Promise.allSettled([...initPromises, timerPromise])

      // Initialize finished
      dispatch(setAppInitialized(true))
    }

    initializeApp()
  }, [dispatch, isLogin, setUser])

  useEffect(() => {
    if (connection)
      dispatch(
        setConnection({
          connection,
          connectionId,
        })
      )
    else
      dispatch(
        setConnection({
          connection: null,
          connectionId: '',
        })
      )
  }, [connection, connectionId, dispatch])

  useEffect(() => {
    if (!connection) return

    const handler = (message: string) => {
      const utterance = new SpeechSynthesisUtterance(message)
      utterance.lang = 'vi-VN'
      window.speechSynthesis.speak(utterance)
    }

    if (isSoundNotif) connection.on('EventUnknowCar', handler)
    else {
      connection.off('EventUnknowCar')
    }

    return () => {
      connection.off('EventUnknowCar')
    }
  }, [isSoundNotif, connection])

  return <>{children}</>
}

export default DefaultAction
